/**
 * Bunny Character Component
 * Animated bunny that hops when tapped with 3 different poses
 */

import { motion, useAnimation } from 'framer-motion';
import { useEffect } from 'react';

interface BunnyProps {
  position: number; // 0-100 percentage across screen
  isHopping: boolean;
  isAtHome: boolean; // New prop to show happy pose
  onHopComplete?: () => void;
}

export function Bunny({ position, isHopping, isAtHome, onHopComplete }: BunnyProps) {
  const controls = useAnimation();

  useEffect(() => {
    if (isHopping) {
      // Hop animation
      controls.start({
        y: [0, -60, 0],
        rotate: [0, -5, 0],
        transition: {
          duration: 0.5,
          ease: 'easeInOut',
        },
      }).then(() => {
        onHopComplete?.();
      });
    }
  }, [isHopping, controls, onHopComplete]);

  // Choose the right Benny pose
  const getBennyImage = () => {
    if (isAtHome) {
      return '/benny-1-03.svg?v=2'; // Happy/Home pose
    } else if (isHopping) {
      return '/benny-1-02.svg?v=2'; // Hopping pose
    } else {
      return '/benny-1-01.svg?v=2'; // Idle/Start pose
    }
  };

  return (
    <motion.div
      className="absolute bottom-24 flex flex-col items-center beni-position-mobile"
      style={{ left: `${position}%` }}
      animate={controls}
      transition={{ left: { duration: 0.5, ease: 'easeInOut' } }}
    >
      {/* Benny the Bunny - Switches between 3 poses! */}
      <motion.div
        className="select-none"
        whileTap={{ scale: 0.9 }}
      >
        <motion.img
          key={getBennyImage()} // Key changes trigger re-render
          src={getBennyImage()}
          alt="Benny the Bunny"
          className="w-[450px] h-[650px] object-contain"
          draggable={false}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        />
      </motion.div>

      {/* Shadow */}
      <motion.div
        className="w-64 h-8 bg-black/20 rounded-full blur-sm mt-2"
        animate={{
          scale: isHopping ? [1, 0.5, 1] : 1,
          opacity: isHopping ? [0.2, 0.1, 0.2] : 0.2,
        }}
        transition={{ duration: 0.5 }}
      />
    </motion.div>
  );
}
